import { Extrapolation, interpolate, SharedValue } from 'react-native-reanimated';
import type { IComputedDirectionTypes } from 'react-native-reanimated-carousel';

interface TBaseConfig {
  size: number;
  vertical: boolean;
}

export interface ILayoutConfig {
  /**
   * control prev/next item offset.
   * @default 100
   */
  parallaxScrollingOffset?: number;
  /**
   * control prev/current/next item offset.
   * @default 0.8
   */
  parallaxScrollingScale?: number;
  /**
   * control prev/next item offset.
   * @default Math.pow(parallaxScrollingScale, 2)
   */
  parallaxAdjacentItemScale?: number;
}

export type TParallaxModeProps = IComputedDirectionTypes<{
  /**
   * Carousel Animated transitions.
   */
  mode?: 'parallax';
  modeConfig?: ILayoutConfig;
}>;

export function parallaxLayout(baseConfig: TBaseConfig, modeConfig: ILayoutConfig = {}) {
  const { size, vertical } = baseConfig;
  const {
    parallaxScrollingOffset = 100,
    parallaxScrollingScale = 0.8,
    parallaxAdjacentItemScale = parallaxScrollingScale ** 2,
  } = modeConfig;

  return (value: number) => {
    'worklet';
    const translate = interpolate(
      value,
      [-1, 0, 1],
      [-size + parallaxScrollingOffset, 0, size - parallaxScrollingOffset]
    );

    const zIndex = interpolate(value, [-1, 0, 1], [0, size, 0], Extrapolation.CLAMP);

    const scale = interpolate(
      value,
      [-1, 0, 1],
      [parallaxAdjacentItemScale, parallaxScrollingScale, parallaxAdjacentItemScale],
      Extrapolation.CLAMP
    );

    const opacity = interpolate(value, [-2, -1, 0, 1, 2], [0, 1, 1, 1, 0], Extrapolation.CLAMP);

    return {
      transform: [
        vertical
          ? {
              translateY: translate,
            }
          : {
              translateX: translate,
            },
        {
          scale,
        },
      ],
      opacity,
      zIndex,
    };
  };
}

export type TTinderBaseConfig = {
  dataLength: number;
  pageWidth: number;
  directionAnimVal: SharedValue<number>;
};

export function tinderLayout(baseConfig: TTinderBaseConfig) {
  const { dataLength, pageWidth, directionAnimVal } = baseConfig;

  return (value: number) => {
    'worklet';
    const translateY = interpolate(value, [0, 1], [0, -18]);

    const translateX = interpolate(value, [-1, 0], [pageWidth, 0], Extrapolation.CLAMP) * directionAnimVal.value;

    const rotateZ = interpolate(value, [-1, 0], [15, 0], Extrapolation.CLAMP) * directionAnimVal.value;

    const zIndex = interpolate(
      value,
      [0, 1, 2, 3, 4],
      [0, 1, 2, 3, 4].map((v) => (dataLength - v) * 10),
      Extrapolation.CLAMP
    );

    const scale = interpolate(value, [0, 1], [1, 0.95]);

    const opacity = interpolate(value, [-1, -0.8, 0, 1], [0, 0.9, 1, 0.85], Extrapolation.EXTEND);

    return {
      transform: [{ translateY }, { translateX }, { rotateZ: `${rotateZ}deg` }, { scale }],
      zIndex,
      opacity,
    };
  };
}
